/**
 * WorkingDocEditor - Lexical-based Markdown Editor (Live Preview)
 *
 * Éditeur riche pour les documents de travail du candidat.
 * Basé sur Lexical (Meta) avec import/export Markdown transparent.
 *
 * Features:
 * - Import/export Markdown automatique (état Lexical ↔ string markdown)
 * - Raccourcis Markdown natifs : `# `, `**text**`, `- `, etc.
 * - Obsidian Live Preview : blocs focalisés montrent la syntaxe markdown brute
 * - Code blocks en Live Preview : rendu visuel ↔ édition (clic pour éditer)
 * - Undo/Redo (Ctrl+Z / Ctrl+Y)
 * - Floating toolbar sur sélection de texte
 * - Listes à puces, numérotées, et cases à cocher
 * - Liens et titres (H1/H2/H3)
 *
 * Architecture:
 * - `LexicalComposer` : root provider, initialise l'état depuis le contenu Markdown
 * - Remontage forcé via `key={docId}` (dans le parent) quand le document change
 * - `OnChangePlugin` : export Markdown debounced → `onContentChange`
 * - `CodeBlockDecoratorNode` : blocs de code en mode Live Preview
 * - Import : `$convertFromMarkdownString` → Node Replacement API → CodeBlockDecoratorNode
 * - Export : transformer dédié CodeBlockDecoratorNode → fenced markdown
 */

import { useCallback, useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import {
  AppSeparatorNode,
  APP_SEPARATOR_TRANSFORMER,
  $createAppSeparatorNode,
} from './AppSeparatorNode';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import {
  TRANSFORMERS,
  ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  CHECK_LIST,
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from '@lexical/markdown';
import { $createParagraphNode, ParagraphNode } from 'lexical';
import type { EditorState } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isCodeNode } from '@lexical/code';
import { LivePreviewPlugin } from './LivePreviewPlugin';
import { FloatingToolbarPlugin } from './FloatingToolbarPlugin';
import { MarkdownPastePlugin } from './MarkdownPastePlugin';
import { CodeBlockPlugin } from './CodeBlockPlugin';
import { CodeBlockDecoratorNode, CODE_BLOCK_DECORATOR_TRANSFORMER } from './CodeBlockDecoratorNode';

// ─── Export transformers ──────────────────────────────────────────────────────
// ELEMENT_TRANSFORMERS covers headings, quotes, lists (no code block).
// TEXT_FORMAT/MATCH cover inline bold/italic/links.
// APP_SEPARATOR + CODE_BLOCK_DECORATOR handle our custom nodes on export.

const EXPORT_TRANSFORMERS = [
  APP_SEPARATOR_TRANSFORMER,
  CODE_BLOCK_DECORATOR_TRANSFORMER,
  CHECK_LIST,
  ...ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
];

// ─── Import + shortcut transformers ──────────────────────────────────────────
// CHECK_LIST placed before TRANSFORMERS (which contains UNORDERED_LIST) so that
// `- [ ] text` is matched by CHECK_LIST first — UNORDERED_LIST would steal it.
// APP_SEPARATOR_TRANSFORMER placed first to take priority over built-in HR.

const ALL_TRANSFORMERS = [APP_SEPARATOR_TRANSFORMER, CHECK_LIST, ...TRANSFORMERS];

// ─── Lexical theme ────────────────────────────────────────────────────────────

const editorTheme = {
  heading: {
    h1: 'wde-h1',
    h2: 'wde-h2',
    h3: 'wde-h3',
  },
  text: {
    bold: 'wde-bold',
    italic: 'wde-italic',
    underline: 'wde-underline',
    strikethrough: 'wde-strikethrough',
    code: 'wde-inlineCode',
  },
  list: {
    ul: 'wde-ul',
    ol: 'wde-ol',
    listitem: 'wde-listItem',
    listitemChecked: 'wde-listItemChecked',
    listitemUnchecked: 'wde-listItemUnchecked',
    nested: {
      listitem: 'wde-nestedListItem',
    },
  },
  link: 'wde-link',
  code: 'wde-codeBlock',
  codeHighlight: {
    atrule: 'wde-tokenAttr',
    attr: 'wde-tokenAttr',
    boolean: 'wde-tokenProperty',
    builtin: 'wde-tokenSelector',
    cdata: 'wde-tokenComment',
    char: 'wde-tokenSelector',
    class: 'wde-tokenFunction',
    'class-name': 'wde-tokenFunction',
    comment: 'wde-tokenComment',
    constant: 'wde-tokenProperty',
    deleted: 'wde-tokenProperty',
    doctype: 'wde-tokenComment',
    entity: 'wde-tokenOperator',
    function: 'wde-tokenFunction',
    important: 'wde-tokenVariable',
    inserted: 'wde-tokenSelector',
    keyword: 'wde-tokenAttr',
    namespace: 'wde-tokenVariable',
    number: 'wde-tokenProperty',
    operator: 'wde-tokenOperator',
    prolog: 'wde-tokenComment',
    property: 'wde-tokenProperty',
    punctuation: 'wde-tokenPunctuation',
    regex: 'wde-tokenVariable',
    selector: 'wde-tokenSelector',
    string: 'wde-tokenSelector',
    symbol: 'wde-tokenProperty',
    tag: 'wde-tokenProperty',
    url: 'wde-tokenOperator',
    variable: 'wde-tokenVariable',
  },
  quote: 'wde-quote',
  paragraph: 'wde-paragraph',
};

// ─── Registered nodes ─────────────────────────────────────────────────────────
// CodeNode is kept to receive $convertFromMarkdownString and MarkdownShortcutPlugin
// output. CodeBlockTransformPlugin (below) converts it to CodeBlockDecoratorNode
// via registerNodeTransform — which runs on every update, covering both import
// and live typing. Node Replacement API cannot be used here because it requires
// withKlass to extend the replaced class (CodeBlockDecoratorNode extends
// DecoratorNode, not CodeNode).

const EDITOR_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  CodeNode,
  CodeHighlightNode,
  AutoLinkNode,
  LinkNode,
  AppSeparatorNode,
  CodeBlockDecoratorNode,
];

// ─── CodeBlock transform plugin ───────────────────────────────────────────────
// Converts any CodeNode (created by $convertFromMarkdownString or by
// MarkdownShortcutPlugin) into a CodeBlockDecoratorNode.
// registerNodeTransform runs on every update, covering both initial import and
// live typing — unlike the manual post-processing it replaces.

function CodeBlockTransformPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerNodeTransform(CodeNode, (node) => {
      if (!$isCodeNode(node)) return;
      const decorator = new CodeBlockDecoratorNode(
        node.getTextContent(),
        node.getLanguage() || 'text',
        false,
      );
      node.replace(decorator);
      // Ensure a navigable paragraph exists after the code block.
      // DecoratorNode is contentEditable=false — without a successor the cursor
      // has nowhere to land when the user exits the block or presses ↓.
      if (decorator.getNextSibling() === null) {
        const paragraph = $createParagraphNode();
        decorator.insertAfter(paragraph);
        // Move selection to the new paragraph so it doesn't point to the
        // removed CodeNode (prevents "selection lost" Lexical error).
        paragraph.select();
      }
    });
  }, [editor]);
  return null;
}

// ─── Separator transform plugin ───────────────────────────────────────────────
// Converts any ParagraphNode whose text is exactly "---" (or "***" / "___")
// into an AppSeparatorNode immediately on each editor update — no Enter required.
// This mirrors the CodeBlockTransformPlugin pattern and is more reliable than
// relying on MarkdownShortcutPlugin's Enter-key handler for element transformers.

function SeparatorTransformPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerNodeTransform(ParagraphNode, (node) => {
      const text = node.getTextContent();
      if (text !== '---' && text !== '***' && text !== '___') return;
      const separator = $createAppSeparatorNode();
      node.replace(separator);
      if (separator.getNextSibling() === null) {
        const paragraph = $createParagraphNode();
        separator.insertAfter(paragraph);
        paragraph.select();
      }
    });
  }, [editor]);
  return null;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkingDocEditorProps {
  docId: string;
  initialContent: string;
  onContentChange: (markdown: string) => void;
  placeholder?: string;
  /**
   * Rend le document en lecture seule : Lexical n'accepte plus d'édition, les
   * plugins d'écriture (raccourcis markdown, collage, barre flottante) ne sont
   * pas montés et `onContentChange` n'est jamais appelé.
   */
  readOnly?: boolean;
  /**
   * `document` (défaut) : pane pleine hauteur, marges de lecture larges.
   * `field` : l'éditeur tient lieu de textarea dans un formulaire — mêmes
   * padding, taille de police et placeholder que `TextInput` / `Textarea`.
   *
   * Les feuilles `.wde-*` vivent hors `@layer` (cf. globals.css) et gagnent
   * donc sur toute utility Tailwind, y compris les variants arbitraires
   * `[&_[contenteditable]]:px-3` que trois vues admin posaient sans effet.
   * La variante est portée par une classe globale pour la même raison.
   */
  variant?: 'document' | 'field';
}

// ─── Component ────────────────────────────────────────────────────────────────

export const WorkingDocEditor = ({
  docId,
  initialContent,
  onContentChange,
  placeholder = 'Commencez à écrire en markdown…',
  readOnly = false,
  variant = 'document',
}: WorkingDocEditorProps) => {
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        editorState.read(() => {
          const markdown = $convertToMarkdownString(EXPORT_TRANSFORMERS);
          onContentChange(markdown);
        });
      }, 500);
    },
    [onContentChange],
  );

  const initialConfig: any = {
    namespace: `working-doc-${docId}`,
    theme: editorTheme,
    editable: !readOnly,
    nodes: EDITOR_NODES,
    editorState: () => {
      $convertFromMarkdownString(initialContent, ALL_TRANSFORMERS);
    },
    onError: (error: Error) => {
      console.error('[WorkingDocEditor]', error);
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        className={
          variant === 'field' ? 'wde-editorWrapper wde-editorWrapper--field' : 'wde-editorWrapper'
        }
      >
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="wde-contentEditable"
              aria-label="Éditeur de document de travail"
            />
          }
          placeholder={
            <div className="wde-placeholder" data-editor-placeholder="">
              {placeholder}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ListPlugin />
        <CheckListPlugin />
        <LinkPlugin />
        <LivePreviewPlugin />
        <CodeBlockTransformPlugin />
        <SeparatorTransformPlugin />
        <CodeBlockPlugin />
        {/* Plugins d'écriture : inutiles en lecture seule, et la barre flottante
            proposerait des actions de formatage sans effet sur un éditeur non
            éditable. */}
        {!readOnly && (
          <>
            <HistoryPlugin />
            <MarkdownShortcutPlugin transformers={ALL_TRANSFORMERS} />
            <FloatingToolbarPlugin />
            <MarkdownPastePlugin />
            <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
          </>
        )}
      </div>
    </LexicalComposer>
  );
};
